/**
 * VehicleSceneTimelinePage — Top-level page component
 *
 * Composes ControlsBar + MultiLaneTimeline + ExplainDrawer
 * into the full Vehicle Scene Viewer experience.
 */
import { Loader2, AlertTriangle, Info, ShieldCheck, ShieldAlert } from 'lucide-react';
import { useVehicleSceneTimeline } from '../../hooks/useVehicleSceneTimeline';
import ControlsBar from './ControlsBar';
import MultiLaneTimeline from './MultiLaneTimeline';
import ExplainDrawer from './ExplainDrawer';
import { formatDateTime } from '../../utils/timelineUtils';
import './VehicleSceneTimeline.css';

export default function VehicleSceneTimelinePage() {
  const {
    data,
    loading,
    error,
    vehicleList,
    vehicleListLoading,
    controls,
    setControls,
    zoomPan,
    setZoomPan,
    selection,
    setSelection,
    selectSegment,
    selectOrder,
    selectDriver,
    selectFuel,
    selectSlice,
    hover,
    setHover,
    explainData,
    explainLoading,
    explainError,
    explainEdge,
    scrubberMs,
    setScrubberMs,
    refresh,
  } = useVehicleSceneTimeline();

  const asOfLabel = controls.asOf ? formatDateTime(controls.asOf) : null;

  return (
    <div className="vst-page">
      {/* Page header */}
      <div className="vst-page-header">
        <h1 className="vst-page-title">Vehicle Scene Timeline</h1>
        <div className="vst-page-subtitle">
          Truth Layer — Graph-first spatiotemporal viewer
        </div>
        {data?.meta.projection_version && (
          <span className="vst-version-badge">
            {data.meta.projection_version}
          </span>
        )}
      </div>

      {/* Controls */}
      <ControlsBar
        controls={controls}
        onChange={setControls}
        onRefresh={refresh}
        loading={loading}
        asOfLabel={asOfLabel}
        vehicleList={vehicleList}
        vehicleListLoading={vehicleListLoading}
      />

      {/* Meta summary bar */}
      {data && (
        <div className="vst-meta-bar">
          <div className="vst-meta-item">
            <Info size={12} />
            {data.segments.length} segments
          </div>
          <div className="vst-meta-item">
            {data.overlays.orders.length} orders
          </div>
          {data.overlays.planned_orders.length > 0 && (
            <div className="vst-meta-item">
              {data.overlays.planned_orders.length} planned
            </div>
          )}
          {data.overlays.deliveries.length > 0 && (
            <div className="vst-meta-item">
              {data.overlays.deliveries.length} delivered
            </div>
          )}
          <div className="vst-meta-item">
            {data.overlays.drivers.length} drivers
          </div>
          <div className="vst-meta-item">
            {data.overlays.fuel.length} fuel events
          </div>
          <div className="vst-meta-item">
            {data.attribution_slices.length} slices
          </div>
          {data.meta.graph_summary && (
            <div className="vst-meta-item">
              {data.meta.graph_summary.total_edges} graph edges
            </div>
          )}
          {data.meta.provenance_summary && (
            <div className="vst-meta-item">
              {data.meta.provenance_summary.total_evidence_events} evidence events
            </div>
          )}
          {controls.asOf && (
            <div className="vst-meta-item vst-asof-indicator">
              as of {asOfLabel}
            </div>
          )}
        </div>
      )}

      {/* Data quality indicator */}
      {data?.data_quality && (
        <div className="vst-data-quality-bar">
          <div className="vst-dq-header">
            {data.data_quality.completeness >= 0.7
              ? <ShieldCheck size={14} className="vst-dq-icon-good" />
              : <ShieldAlert size={14} className="vst-dq-icon-warn" />
            }
            <span className="vst-dq-label">Data Quality</span>
            <span className="vst-dq-score">
              {Math.round(data.data_quality.completeness * 100)}%
            </span>
            <div className="vst-dq-bar-track">
              <div
                className="vst-dq-bar-fill"
                style={{
                  width: `${Math.round(data.data_quality.completeness * 100)}%`,
                  background: data.data_quality.completeness >= 0.7 ? '#10b981'
                    : data.data_quality.completeness >= 0.4 ? '#f59e0b'
                    : '#ef4444',
                }}
              />
            </div>
          </div>
          {data.data_quality.issues.length > 0 && (
            <div className="vst-dq-issues">
              {data.data_quality.issues.map((issue, idx) => (
                <span
                  key={idx}
                  className={`vst-dq-issue vst-dq-${issue.severity}`}
                >
                  {issue.message}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main content area */}
      <div className="vst-main">
        {/* Loading state */}
        {loading && !data && (
          <div className="vst-loading">
            <Loader2 size={32} className="animate-spin" />
            <span>Loading timeline...</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="vst-error">
            <AlertTriangle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Timeline */}
        {data && (
          <div className="vst-timeline-and-drawer">
            <div className={`vst-timeline-panel ${selection ? 'with-drawer' : ''}`}>
              <MultiLaneTimeline
                data={data}
                viewMode={controls.viewMode}
                lanes={controls.lanes}
                zoomPan={zoomPan}
                onZoomPan={setZoomPan}
                selection={selection}
                selectSegment={selectSegment}
                selectOrder={selectOrder}
                selectDriver={selectDriver}
                selectFuel={selectFuel}
                selectSlice={selectSlice}
                hover={hover}
                onHover={setHover}
                scrubberMs={controls.playheadEnabled ? scrubberMs : null}
                onScrub={controls.playheadEnabled ? setScrubberMs : () => {}}
              />

              {/* Usage hints */}
              <div className="vst-hints">
                <span>Scroll to pan</span>
                <span>Ctrl+Scroll to zoom</span>
                <span>Click items for details</span>
                {controls.playheadEnabled && (
                  <span>Drag playhead for time inspection</span>
                )}
              </div>
            </div>

            {/* Explain drawer */}
            <ExplainDrawer
              selection={selection}
              explainData={explainData}
              explainLoading={explainLoading}
              explainError={explainError}
              onExplainEdge={explainEdge}
              onClose={() => setSelection(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
